import React, { useEffect, useMemo } from 'react';
import { Modal } from 'react-bootstrap';
import { Controller, useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router';
import PratusSelect, {
  OptionType,
} from 'components/DesignSystems/New/PratusSelect';
import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import { useGetPositionsForIncident } from 'hooks/incident/useRoster';
import { useIncidentRoster } from 'components/IAP/hooks/incidentRoster/useIncidentRoster';
import { useGetDraftRosterMembersForIncident } from 'components/IAP/hooks/incidentRoster/useGetDraftRosterMembersForIncident';
import { useResourcesInIncident } from 'components/IAP/hooks/resourceInIncidentHooks';
import {
  RosterResource,
  useAddResourceToRoster,
  useRemoveResourceFromRoster,
} from 'components/IAP/hooks/incidentRosterResources/incidentRosterResources';

type AddResourceToRosterModalProps = {
  show: boolean;
  onClose: () => void;
  editingResource?: RosterResource | null;
  /** When set, pre-fills and hides the "Reports to" selector with this position. */
  presetPositionId?: string;
};

type FormValues = {
  resourceInIncidentId: string;
  positionId: string;
  pointOfContactUserGuid: string;
};

const isPersonnel = (value?: string | null) =>
  (value ?? '').toLowerCase() === 'personnel';

const pickOption = (opt: unknown): OptionType | null => {
  if (!opt) return null;
  if (Array.isArray(opt)) return (opt[0] as OptionType | undefined) ?? null;
  return opt as OptionType;
};

const AddResourceToRosterModal: React.FC<AddResourceToRosterModalProps> = ({
  show,
  onClose,
  editingResource = null,
  presetPositionId,
}) => {
  const isEditing = editingResource !== null;
  const [searchParams] = useSearchParams();
  const isDraft = searchParams.get('mode') === 'draft';
  const positionsQuery = useGetPositionsForIncident();
  const activeRoster = useIncidentRoster();
  const draftRoster = useGetDraftRosterMembersForIncident();
  const roster = isDraft ? draftRoster : activeRoster;
  const resourcesQuery = useResourcesInIncident();
  const { mutate: addResource, isPending: isAdding } = useAddResourceToRoster();
  const { mutate: removeResource, isPending: isRemoving } =
    useRemoveResourceFromRoster();

  const defaultValues = useMemo<FormValues>(
    () => ({
      resourceInIncidentId: editingResource?.resourceInIncidentId ?? '',
      positionId: editingResource?.reportsToPosition ?? presetPositionId ?? '',
      pointOfContactUserGuid:
        editingResource?.pointOfContactUser?.userGuid ??
        editingResource?.resourceSet.pointOfContact ??
        '',
    }),
    [editingResource, presetPositionId]
  );

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    reset,
  } = useForm<FormValues>({ mode: 'onChange', defaultValues });

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset, show]);

  const resourceOptions = useMemo<OptionType[]>(() => {
    const resources = resourcesQuery.data ?? [];
    return resources
      .filter(
        (r) =>
          !isPersonnel(r.resource_set_kind) && !isPersonnel(r.resource_set_type)
      )
      .map((r) => ({
        value: r.resource_in_incident_id,
        label:
          r.resource_name ??
          r.resource_name_identifier ??
          `${r.resource_set_kind ?? 'Resource'} (${r.resource_in_incident_id.slice(0, 8)})`,
      }));
  }, [resourcesQuery.data]);

  const positionOptions = useMemo<OptionType[]>(
    () =>
      (positionsQuery.data ?? []).map((pos) => ({
        value: pos.id,
        label: pos.name,
      })),
    [positionsQuery.data]
  );

  const pocOptions = useMemo<OptionType[]>(
    () =>
      (roster.data ?? [])
        .filter(
          (member) => member.userId && member.incidentPositions.length > 0
        )
        .map((member) => ({
          value: member.userId,
          label:
            member.userName ??
            member.emailAddress ??
            `Member ${member.userId.slice(0, 8)}`,
        })),
    [roster.data]
  );

  const handleClose = () => {
    reset();
    onClose();
  };

  const onSubmit = (values: FormValues) => {
    addResource(values, { onSuccess: handleClose });
  };

  const onRemove = () => {
    if (!editingResource) return;
    removeResource(editingResource.resourceInIncidentId, {
      onSuccess: handleClose,
    });
  };

  const isPending = isAdding || isRemoving;
  const title = isEditing ? 'Edit Roster Resource' : 'Add Resource to Roster';
  const submitLabel = isEditing ? 'Save Changes' : 'Add Resource';
  const submittingLabel = isEditing ? 'Saving…' : 'Adding…';

  return (
    <Modal show={show} onHide={handleClose} centered size="lg">
      <form onSubmit={handleSubmit(onSubmit)}>
        <Modal.Header closeButton closeVariant="white">
          <Modal.Title>{title}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="d-flex flex-column gap-3">
          {isEditing ? (
            <PratusSelect
              label="Resource"
              inputId="resourceInIncidentId_disabled"
              options={[]}
              value={{
                label: editingResource?.resourceSet.name ?? '',
                value: editingResource?.resourceInIncidentId ?? '',
              }}
              onChange={() => {}}
              isDisabled
            />
          ) : (
            <Controller
              name="resourceInIncidentId"
              control={control}
              rules={{ required: 'Select a resource' }}
              render={({ field }) => (
                <PratusSelect
                  label="Resource"
                  inputId="resourceInIncidentId"
                  options={resourceOptions}
                  value={
                    resourceOptions.find((opt) => opt.value === field.value) ??
                    null
                  }
                  onChange={(opt) => {
                    const single = pickOption(opt);
                    field.onChange(String(single?.value ?? ''));
                  }}
                  placeholder="Select a non-personnel resource"
                  isClearable
                />
              )}
            />
          )}
          {errors.resourceInIncidentId && (
            <div className="text-danger small">
              {errors.resourceInIncidentId.message}
            </div>
          )}
          <Controller
            name="pointOfContactUserGuid"
            control={control}
            rules={{ required: 'Select a point of contact' }}
            render={({ field }) => (
              <PratusSelect
                label="Point of Contact"
                inputId="pointOfContactUserGuid"
                options={pocOptions}
                value={
                  pocOptions.find((opt) => opt.value === field.value) ?? null
                }
                onChange={(opt) => {
                  const single = pickOption(opt);
                  field.onChange(String(single?.value ?? ''));
                }}
                placeholder="Select a roster member"
                isClearable
              />
            )}
          />
          {errors.pointOfContactUserGuid && (
            <div className="text-danger small">
              {errors.pointOfContactUserGuid.message}
            </div>
          )}
          {!presetPositionId && (
            <>
              <Controller
                name="positionId"
                control={control}
                rules={{ required: 'Select a position' }}
                render={({ field }) => (
                  <PratusSelect
                    label="Reports to"
                    inputId="positionId"
                    options={positionOptions}
                    value={
                      positionOptions.find(
                        (opt) => opt.value === field.value
                      ) ?? null
                    }
                    onChange={(opt) => {
                      const single = pickOption(opt);
                      field.onChange(String(single?.value ?? ''));
                    }}
                    placeholder="Select a position"
                    isClearable
                  />
                )}
              />
              {errors.positionId && (
                <div className="text-danger small">
                  {errors.positionId.message}
                </div>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-between">
          <div>
            {isEditing && (
              <StylishNewButton
                className="button--danger"
                type="button"
                onClick={onRemove}
                disabled={isPending}
              >
                {isRemoving ? 'Removing…' : 'Remove'}
              </StylishNewButton>
            )}
          </div>
          <div className="d-flex gap-2">
            <StylishNewButton
              className="button--secondary"
              type="button"
              onClick={handleClose}
            >
              Cancel
            </StylishNewButton>
            <StylishNewButton
              className="button--primary"
              type="submit"
              disabled={!isValid || isPending}
            >
              {isAdding ? submittingLabel : submitLabel}
            </StylishNewButton>
          </div>
        </Modal.Footer>
      </form>
    </Modal>
  );
};

export default AddResourceToRosterModal;
